import { createTheme } from '@mui/material/styles'
import colors from './colors.json'
import { esES } from '@mui/material/locale'
import { esES as esES_datagrid } from 'vendor/x-data-grid'

/**
 * Data grid function _props
 */

const rootFontSize = parseInt(
  getComputedStyle(document.documentElement).getPropertyValue(
    '--root-font-size'
  )
)

const theme = createTheme(
  {
    palette: {
      type: 'light',
      primary: colors.primary,
      secondary: colors.tertiary,
      tertiary: colors.secondary,
      black: {
        main: colors.neutral.black,
      },
      gray3: {
        main: colors.neutral.gray3,
      },
      gray2: {
        main: colors.neutral.gray2,
      },
      gray1: {
        main: colors.neutral.gray1,
      },
      white: {
        main: colors.neutral.white,
      },
      bg: {
        main: colors.neutral.bg,
      },
      error: {
        main: colors.feedback.error,
      },
      success: {
        main: colors.feedback.success,
      },
      background: {
        default: colors.neutral.bg,
        paper: colors.neutral.white,
      },
      text: {
        primary: colors.neutral.black,
        secondary: colors.neutral.black + 'cc',
        disabled: colors.neutral.black + '66',
        hint: colors.neutral.black + '66',
      },
    },
    components: {
      MuiButtonBase: {
        defaultProps: {
          disableRipple: true, // No more ripple, on the whole application 💣!
          color: 'primary',
        },
      },
      MuiDialogTitle: {
        defaultProps: {
          color: 'primary',
          variant: 'h1',
        },
        styleOverrides: {
          root: {
            padding: '0px',
          },
        },
      },
      MuiDialogActions: {
        styleOverrides: {
          root: {
            justifyContent: 'center',
          },
        },
      },
      MuiButton: {
        styleOverrides: {
          root: {
            '--border-size': '3px',
            '--padding-y': 'calc(1rem - 1px)',
            '--padding-x': '3rem',
            '--space-between-icon-label': 'calc(1rem - 1px)',
            height: 'auto',
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            borderRadius: '0.625rem',
            '& *(:not(:first-of-type))': {
              paddingLeft: 'var(--space-between-icon-label)',
            },
            '&.MuiButton-sizeLarge': {
              minWidth: '25rem',
            },
            '&.MuiButton-sizeGrid': {
              padding: '0.3em 0.5em',
              lineHeight: '1',
            },
          },
          contained: {
            backgroundImage: `linear-gradient(90deg, ${colors.primary.main} 0%, ${colors.tertiary.main} 100%)`,
            backgroundSize: 'cover',
            boxShadow: 'none',
            color: 'white',
            height: 'auto',
            padding: 'var(--padding-y) var(--padding-x)',
            '&.Mui-disabled': {
              color: 'white',
              opacity: '0.3',
            },
          },
          outlined: {
            backgroundImage: `white`,
            color: colors.primary.main,
            border: `3px solid ${colors.primary.main}`,
            boxShadow: 'none',
            height: 'auto',
            padding:
              'calc( var(--padding-y) - var(--border-size)) calc( var(--padding-x) - var(--border-size))',
            '&.MuiButton-outlinedSecondary': {
              color: colors.neutral.black,
              border: `3px solid ${colors.neutral.black}`,
            },
            '&:hover': {
              border: `3px solid ${colors.primary.main}`,
            },
          },
          underlined: {
            color: 'inherit',
            position: 'relative',
            fontSize: 'inherit',
            //marginBottom: "2em",
            padding: 0,
            transition:
              'background-color 0.3s ease-in-out, opacity 0.3s ease-in-out',
            fontWeight: '500',
            '&:hover': {
              backgroundColor: 'transparent',
            },
            '&.active': {
              fontWeight: '700',
            },
            '&:hover:not(.active)': {
              opacity: '0.6',
            },
            '&:after': {
              content: "''",
              transition: 'width 0.5s ease-in-out',
              position: 'absolute',
              bottom: '-0.33em',
              width: '0',
              opacity: 'inherit',
              left: '0',
              height: '0.166em',
              backgroundColor: 'transparent',
            },
            '&.active:after': {
              backgroundColor: 'currentColor',
              width: '100%',
            },
          },
          dot: {
            '--dot-size': '1rem',
            backgroundColor: colors.neutral.white,
            borderRadius: '100%',
            maxHeight: 'var(--dot-size)',
            maxWidth: 'var(--dot-size)',
            minHeight: 'var(--dot-size)',
            minWidth: 'var(--dot-size)',
            transition: 'background-color 0.5s ease-in-out',
            cursor: 'pointer',
            padding: '0',
            '&.active': {
              backgroundColor: colors.primary.main,
            },
            '&:hover:not(.active)': {
              backgroundColor: colors.tertiary.main,
            },
          },
          text: {
            color: colors.primary.main,
            fontWeight: '700',
            fontSize: '1rem',
            padding: 0,
            textTransform: 'none',
          },
        },
      },
      MuiFormControl: {
        styleOverrides: {
          root: {
            fontSize: '1rem',
          },
        },
      },
      MuiFormLabel: {
        styleOverrides: {
          root: {
            fontSize: 'inherit',
            marginBottom: '0.625rem',
            '&.Mui-error': {
              color: colors.neutral.black,
            },
          },
        },
      },
      MuiInputBase: {
        styleOverrides: {
          root: {
            whiteSpace: 'nowrap',
            fontSize: 'inherit',
            backgroundColor: 'white',
            boxSizing: 'border-box',
            color: colors.neutral.black,
            border: `1px solid ${colors.neutral.black}`,
            borderRadius: '0.625rem',
            textTransform: 'uppercase',
            '&.Mui-error': {
              border: `2px solid ${colors.feedback.error}`,
            },
            'label + &': {
              marginTop: '0.4rem',
            },
            '& .MuiInputBase-input': {
              borderRadius: '0.625rem',
              position: 'relative',
              backgroundColor: 'white',
              fontSize: '1rem',
              padding: '0.875rem 0.75rem',
              flex: '1 1 auto',
              textTransform: 'uppercase',
            },
            '& .MuiInputBase-input[name="email"], & .MuiInputBase-input[name="password"], & .MuiInputBase-input[name="passwordConfirmation"], & .MuiInputBase-input[name="oldPassword"]':
              {
                textTransform: 'initial',
              },
            '& .MuiButtonBase-root': {
              padding: 0,
            },
            '&.Mui-disabled': {
              backgroundColor: colors.neutral.gray1,
              border: `1px solid ${colors.neutral.gray1}`,
            },
            '& input.Mui-disabled': {
              backgroundColor: colors.neutral.gray1,
            },
            '& .MuiSelect-select.Mui-disabled': {
              backgroundColor: colors.neutral.gray1,
            },
            '& .MuiSelect-select.MuiSelect-outlined.MuiInputBase-input:focus': {
              borderRadius: '0.625rem',
            },
          },
        },
      },
      MuiOutlinedInput: {
        styleOverrides: {
          root: {
            borderRadius: '0.625rem',
          },
        },
      },
      MuiInputAdornment: {
        styleOverrides: {
          root: {
            margin: 0,
            width: '2em',
            display: 'flex',
            flexDirection: 'row',
            alignItems: 'center',
            justifyContent: 'around',
            '& .MuiButtonBase-root': {
              fontSize: 'inherit',
            },
          },
        },
      },
      MuiFormHelperText: {
        styleOverrides: {
          root: {
            fontSize: '0.85em',
            fontWeight: '400',
            marginLeft: '0',
          },
        },
      },
      MuiPaper: {
        styleOverrides: {
          root: {
            /*           padding: "50px",*/
            borderRadius: '0.625rem',
            '&.MuiDialog-paper': {
              padding: '20px',
            },
          },
        },
      },
      MuiAppBar: {
        styleOverrides: {
          root: {
            height: '4.5rem',
            padding: '0',
            borderRadius: '0px',
            backgroundImage: `linear-gradient(90deg, ${colors.primary.main} 0%, ${colors.tertiary.main} 100%)`,
            display: 'flex',
            flexDirection: 'row',
            alignItems: 'center',
            justifyContent: 'stretch',
            '& .logo': {
              height: '3.25rem',
              width: 'auto',
            },
            '& .menu': {
              fontSize: '1.125rem',
              fontWeight: '400',
              textTransform: 'uppercase',
            },
          },
        },
      },
      MuiAvatar: {
        styleOverrides: {
          root: {
            border: '2px white solid',
          },
        },
      },
      MuiDataGrid: {
        defaultProps: {
          rowsPerPageOptions: [10, 20, 50, 100],
          disableSelectionOnClick: true,
          keepNonExistentRowsSelected: true,
          checkboxSelection: true,
          paginationMode: 'server',
          sortingMode: 'server',
          rowHeight: rootFontSize * 3.3,
          disableColumnFilter: true,
          disableColumnMenu: true,
        },
        styleOverrides: {
          root: {
            border: 0,
            '& .MuiDataGrid-columnHeaders': {
              fontWeight: 700,
              fontSize: '1rem',
              lineHeight: '2.125rem',
              border: 0,
            },
            '& .MuiDataGrid-virtualScrollerRenderZone': {
              marginRight: '0',
            },
            '& .MuiDataGrid-columnHeaderTitle': {
              fontWeight: 700,
              fontSize: '1.25rem',
              lineHeight: '2.125rem',
            },
            '& .MuiDataGrid-row': {
              boxSizing: 'border-box',
              background: 'white',
              border: `1px solid ${colors.neutral.gray1}`,
              borderRadius: '0.625rem',
            },
            '& .MuiDataGrid-cell': {
              border: 0,
              fontWeight: '400',
              fontSize: '1.25rem',
              lineHeight: '1.5',
              color: colors.neutral.black,
            },
            '& .MuiDataGrid-columnSeparator': {
              display: 'none',
            },
            '& .MuiDataGrid-footerContainer': {
              flexDirection: 'row-reverse',
              borderTop: 'none',
              paddingTop: '2rem',
              '& .MuiDataGrid-selectedRowCount': {
                display: 'none',
              },
              '& .MuiButton-outlinedSecondary': {
                color: colors.neutral.black,
                border: `1px solid ${colors.neutral.black}`,
                textTransform: 'none',
                padding: 'calc(1rem - 1px) 0.625rem',
                fontWeight: 400,
              },
            },
          },
        },
      },
      MuiSwitch: {
        styleOverrides: {
          root: {
            width: 28,
            height: 16,
            padding: 0,
            display: 'flex',
            '&:active': {
              '& .MuiSwitch-thumb': {
                width: 15,
              },
              '& .MuiSwitch-switchBase.Mui-checked': {
                transform: 'translateX(9px)',
              },
            },
            '& .MuiSwitch-switchBase': {
              padding: 2,

              '&.Mui-checked': {
                transform: 'translateX(12px)',
                color: '#fff',
                '& + .MuiSwitch-track': {
                  opacity: 1,
                  backgroundColor: colors.primary.main,
                },
              },
            },
            '& .MuiSwitch-thumb': {
              boxShadow: '0 2px 4px 0 rgb(0 35 11 / 20%)',
              width: 12,
              height: 12,
              borderRadius: 6,
              transition: 'width 0.2s ease-in-out',
            },
            '& .MuiSwitch-track': {
              borderRadius: 16 / 2,
              opacity: 1,
              backgroundColor: colors.neutral.gray2,
              boxSizing: 'border-box',
            },
          },
        },
      },
      MuiRadio: {
        defaultProps: {
          checkedIcon: <span className="checked-icon" />,
        },
        styleOverrides: {
          root: {
            fontSize: '1.25rem',
            '--width': '1em',
            '--height': '1em',
            borderRadius: '50%',
            width: 'var(--width)',
            height: 'var(--height)',
            boxShadow:
              'inset 0 0 0 1px rgba(16,22,26,.2), inset 0 -1px 0 rgba(16,22,26,.1)',
            backgroundColor: colors.neutral.white,
            marginRight: '0.25em',
            backgroundImage:
              'linear-gradient(180deg,hsla(0,0%,100%,.8),hsla(0,0%,100%,0))',
            '& .Mui-focusVisible &': {
              outline: '2px auto rgba(19,124,189,.6)',
              outlineOffset: 2,
            },
            '& input:hover ~ &': {
              backgroundColor: colors.primary.main,
            },
            '& input:disabled ~ &': {
              boxShadow: 'none',
              background: colors.neutral.gray2,
            },
            '& .checked-icon': {
              backgroundColor: colors.primary.main,
              backgroundImage:
                'linear-gradient(180deg,hsla(0,0%,100%,.1),hsla(0,0%,100%,0))',
              width: 'var(--width)',
              height: 'var(--height)',
              position: 'relative',

              borderRadius: 'inherit',
              '&:before': {
                display: 'block',
                width: 'var(--width)',
                height: 'var(--height)',
                backgroundImage:
                  'radial-gradient(#fff,#fff 28%,transparent 32%)',
                content: '""',
              },
              '& input:hover ~ &': {
                backgroundColor: '#106ba3',
              },
            },
          },
        },
      },
      MuiSnackbar: {
        styleOverrides: {
          root: {
            minWidth: '25rem',
            borderRadius: '0.625rem',
            color: colors.neutral.white,
            display: 'flex',
            flexDirection: 'row',
            alignItems: 'center',
            justifyContent: 'stretch',
            '& .MuiPaper-root': {
              width: '100%',
              padding: '10px 20px',
              display: 'flex',
              flexDirection: 'row',
              alignItems: 'center',
              justifyContent: 'flex-start',
              color: 'inherit',
            },
            '& .MuiAlert-message': {
              flex: '1 1 auto',
              display: 'flex',
              flexDirection: 'row',
              alignItems: 'center',
              justifyContent: 'space-between',
            },
            '& .MuiButtonBase-root': {
              color: 'inherit',
              '&:hover': {
                backgroundColor: 'rgba(255,255,255, 0.05)',
              },
            },
          },
        },
      },
    },
    shape: {
      borderRadius: 3,
    },
    typography: {
      fontFamily: ['"Dosis"', 'sans-serif'].join(','),
      fontSize: 16,
      fontWeightLight: 400,
      h1: {
        fontSize: '2.75rem',
        fontWeight: 600,
        lineHeight: '1.265',
      },
      h2: {
        fontSize: '2.25rem',
        fontWeight: 400,
        lineHeight: '1.22',
        color: colors.primary.main,
      },
      h3: {
        fontSize: '1.75rem',
        fontWeight: 400,
        lineHeight: '1.265',
      },
      h4: {
        fontSize: '1.5rem',
        fontWeight: 400,
        lineHeight: '1.4583',
      },
      h5: {
        fontSize: '1.25rem',
        fontWeight: 400,
        lineHeight: '1.5',
      },
      body1: {
        fontSize: '1rem',
        fontWeight: 400,
        lineHeight: '1.56',
      },
      body2: {
        fontSize: '1rem',
        fontWeight: 600,
        lineHeight: '1.56',
      },
      containedInputMock: {
        backgroundColor: colors.neutral.gray1,
        padding: '0.875rem 0.75rem',
        color: colors.neutral.black,
        lineHeight: '1',
        display: 'flex',
        alignItems: 'center',
        borderRadius: '0.625rem',
        justifyContent: 'flex-start',
        width: '100%',
      },
      caption: {
        fontSize: '0.75rem',
        fontWeight: 600,
        lineHeight: '1.56',
      },
      caption1: {
        fontSize: '0.875rem',
        fontWeight: 700,
        lineHeight: '1.56',
      },
      button: {
        fontSize: '1.125rem',
        fontWeight: 700,
        lineHeight: 1.55,
        letterSpacing: '-0.005em',
        textTransform: 'uppercase',
      },
    },
  },
  esES_datagrid,
  esES
)

export default theme
